import { projectSchema } from '@si/types';
import { get, objectToQueryParams } from '@si/utils';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { queryKeys } from './queryKeys';
import { useUser } from './useUser';

const projectUnavailableError = new Error('Project not available');

interface Params {
  projectId: string;
  noRetry?: boolean;
  includeCompany?: boolean;
  includeTeam?: boolean;
  includeGatewayStatus?: boolean;
  includeSensorStatus?: boolean;
  sensorStatusReferenceDate?: Date;
}

export function useProject({
  projectId,
  noRetry = false,
  includeCompany = false,
  includeTeam = false,
  includeGatewayStatus = false,
  includeSensorStatus = false,
  sensorStatusReferenceDate,
}: Params) {
  const { user, isLoading: isLoadingUser } = useUser();
  const url = `/projects/${projectId}${objectToQueryParams({
    includeCompany,
    includeTeam,
    includeGatewayStatus,
    includeSensorStatus,
    sensorStatusReferenceDate,
  })}`;

  const {
    data: project,
    error,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: queryKeys.getProject(
      projectId,
      includeCompany,
      includeTeam,
      includeGatewayStatus,
      includeSensorStatus,
      sensorStatusReferenceDate
    ),
    queryFn: () => get({ url, schema: projectSchema }),
    enabled: !!projectId,
    retry: (failureCount, error) =>
      !noRetry &&
      !(isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0)) &&
      failureCount < 3,
  });

  const projectUnavailable = Boolean(
    project &&
    !user?.isSiAdmin &&
    (project.isUserHidden || (project.projectStage === 'archived' && !user?.isDistributorUser))
  );

  const accessDenied =
    projectUnavailable || (isAxiosError(error) && error.response?.status === 403);

  return {
    accessDenied,
    project: projectUnavailable ? undefined : project,
    error: projectUnavailable && !isLoadingUser ? projectUnavailableError : error,
    isLoading: isLoading || (projectUnavailable && isLoadingUser),
    isRefetching,
    refetch,
  };
}
